import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Upload, Download, Search, Edit2, Trash2, Loader2, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { subscribeOptionsChanged } from "@/lib/optionsBus";
import SellDialog from "@/components/inventory/SellDialog";
import InventoryFormDialog from "@/components/inventory/InventoryFormDialog";

const empty = {
  batch_code: "",
  model_name: "",
  serial_number: "",
  configuration: "",
  device_category: "",
  testing_details: [],
  testing_status: "Pending",
};

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSheet, setOpenSheet] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [previewAssetId, setPreviewAssetId] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [stockFilter, setStockFilter] = useState("ALL");
  const [batchFilter, setBatchFilter] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({ totals: { total: 0, available: 0, sold: 0, holding: 0 }, models: [] });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [sellItem, setSellItem] = useState(null);
  const [sellForm, setSellForm] = useState(null);
  const [selling, setSelling] = useState(false);
  const [unsellConfirm, setUnsellConfirm] = useState(null);
  const fileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const openSell = (it) => {
    setSellItem(it);
    setSellForm(it.stock_status === "Sold"
      ? { customer_name: it.sold_to || "", mobile: it.sold_mobile || "", email: it.sold_email || "", gst: it.sold_gst || "", sale_price: it.sale_price ?? "", sale_date: it.sale_date || new Date().toISOString().slice(0, 10), remarks: it.sale_remarks || "" }
      : { customer_name: "", mobile: "", email: "", gst: "", sale_price: "", sale_date: new Date().toISOString().slice(0, 10), remarks: "" });
  };
  const saveSell = async () => {
    if (!sellForm.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (sellForm.sale_price === "" || Number(sellForm.sale_price) < 0) { toast.error("Enter a valid sale price"); return; }
    setSelling(true);
    try {
      if (sellItem.stock_status === "Sold") {
        await api.put(`/inventory/${sellItem.id}`, { sale_price: Number(sellForm.sale_price), sold_to: sellForm.customer_name, sold_mobile: sellForm.mobile, sold_email: sellForm.email, sold_gst: sellForm.gst, sale_date: sellForm.sale_date, sale_remarks: sellForm.remarks });
        toast.success("Sale details updated");
      } else {
        await api.post(`/inventory/${sellItem.id}/sell`, { customer_name: sellForm.customer_name, mobile: sellForm.mobile, email: sellForm.email, gst: sellForm.gst, sale_price: Number(sellForm.sale_price), sale_date: sellForm.sale_date, remarks: sellForm.remarks });
        toast.success("Item marked as Sold");
      }
      setSellItem(null); fetchItems();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSelling(false); }
  };
  const unsell = async (it) => {
    try { await api.post(`/inventory/${it.id}/unsell`); toast.success("Returned to stock"); setUnsellConfirm(null); setSellItem(null); fetchItems(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (batchFilter.trim()) params.batch = batchFilter.trim().toUpperCase();
      if (statusFilter !== "ALL") params.status_filter = statusFilter;
      if (stockFilter !== "ALL") params.stock = stockFilter;
      const { data } = await api.get("/inventory", { params });
      setItems(data);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const { data } = await api.get("/inventory/summary");
      setSummary(data);
    } catch (e) { console.error("Failed to fetch inventory summary:", e); }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await api.get("/options/device_categories");
      setCategories(data || []);
    } catch (e) { console.error("Failed to fetch device categories:", e); }
  };

  useEffect(() => {
    fetchItems(); fetchSummary(); fetchCategories();
    const unsub = subscribeOptionsChanged("device_categories", fetchCategories);
    return () => unsub();
    /* eslint-disable-next-line */
  }, []);
  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, statusFilter, stockFilter, batchFilter]);

  // Asset ID preview
  useEffect(() => {
    if (editing) return;
    const code = form.batch_code.trim().toUpperCase();
    if (!code || !/^[A-Z]+$/.test(code)) { setPreviewAssetId(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/inventory/next-asset-id/${code}`);
        if (!cancelled) setPreviewAssetId(data.asset_id);
      } catch (e) { if (!cancelled) setPreviewAssetId(""); }
    })();
    return () => { cancelled = true; };
  }, [form.batch_code, editing]);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setPreviewAssetId("");
    setOpenSheet(true);
  };

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      batch_code: item.batch_code,
      model_name: item.model_name,
      serial_number: item.serial_number,
      configuration: item.configuration,
      device_category: item.device_category || "",
      testing_details: item.testing_details || [],
      testing_status: item.testing_status,
    });
    setPreviewAssetId(item.asset_id);
    setOpenSheet(true);
  };

  const save = async () => {
    if (!form.batch_code.trim() || !/^[A-Za-z]+$/.test(form.batch_code.trim())) {
      toast.error("Batch code must be alphabetic (e.g., AA, AB, Z)");
      return;
    }
    if (!form.model_name.trim() || !form.serial_number.trim()) {
      toast.error("Model and Serial number are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/inventory/${editing.id}`, {
          model_name: form.model_name,
          serial_number: form.serial_number,
          configuration: form.configuration,
          device_category: form.device_category,
          testing_details: form.testing_details,
          testing_status: form.testing_status,
        });
        setItems((prev) => prev.map((it) => (it.id === editing.id ? data : it)));
        toast.success("Inventory item updated");
        fetchSummary();
      } else {
        const { data } = await api.post("/inventory", { ...form, batch_code: form.batch_code.toUpperCase() });
        setItems((prev) => [data, ...prev]);
        toast.success("Inventory entry created");
        fetchSummary();
      }
      setOpenSheet(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const id = deleteId;
    const prev = items;
    setItems((p) => p.filter((x) => x.id !== id));
    setDeleteId(null);
    try {
      await api.delete(`/inventory/${id}`);
      toast.success("Item deleted");
      fetchSummary();
    } catch (e) {
      setItems(prev);
      toast.error(formatApiError(e));
    }
  };

  const handleExport = () => {
    const token = localStorage.getItem("access_token");
    fetch(`${API}/exports/inventory.csv`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => { if (!r.ok) throw new Error("Export failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "inventory.csv"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  const handleTemplate = () => {
    const token = localStorage.getItem("access_token");
    fetch(`${API}/exports/inventory-template.csv`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => { if (!r.ok) throw new Error("Template failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "inventory-template.csv"; a.click();
        URL.revokeObjectURL(url);
        toast.success("Template downloaded · fill it in and use Import");
      })
      .catch(() => toast.error("Template download failed"));
  };

  const handleImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/imports/inventory.csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported}, skipped ${data.skipped}`);
      // refetch once after a bulk import (multiple new rows)
      fetchItems(); fetchSummary();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const allSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const toggleSelect = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(items.map((i) => i.id)));
  const bulkDelete = async () => {
    const ids = [...selectedIds];
    setBulkConfirm(false);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/inventory/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted}${data.skipped_sold ? `, skipped ${data.skipped_sold} sold` : ""}`);
      setSelectedIds(new Set());
      fetchItems(); fetchSummary();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Module 01</div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Inventory & Testing</h1>
          <p className="text-sm text-[#475569] mt-1">Track devices from intake to ready-for-sale.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input type="file" ref={fileRef} accept=".csv" onChange={handleImport} className="hidden" data-testid="import-file-input" />
          <Button variant="outline" onClick={handleTemplate} data-testid="template-btn" className="gap-2">
            <Download className="w-4 h-4" /> Template
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-btn" className="gap-2">
            <Upload className="w-4 h-4" /> Import CSV
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-btn" className="gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
          <Button onClick={openNew} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="add-inventory-btn">
            <Plus className="w-4 h-4" /> New entry
          </Button>
        </div>
      </div>

      {/* Stock count cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-4 bg-white border border-[#E2E8F0] rounded-lg" data-testid="inv-count-total">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">Total Stock</div>
          <div className="text-2xl font-display font-semibold mt-1">{summary.totals.total}</div>
        </div>
        <div className="p-4 bg-white border border-[#E2E8F0] rounded-lg" data-testid="inv-count-available">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">Available</div>
          <div className="text-2xl font-display font-semibold mt-1 text-[#22C55E]">{summary.totals.available}</div>
        </div>
        <div className="p-4 bg-white border border-[#E2E8F0] rounded-lg" data-testid="inv-count-sold">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">Sold</div>
          <div className="text-2xl font-display font-semibold mt-1 text-[#0055FF]">{summary.totals.sold}</div>
        </div>
        <div className="p-4 bg-white border border-[#E2E8F0] rounded-lg" data-testid="inv-count-holding">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">Holding</div>
          <div className="text-2xl font-display font-semibold mt-1 text-[#F59E0B]">{summary.totals.holding}</div>
        </div>
      </div>

      <Tabs defaultValue="list">
        <TabsList data-testid="inv-tabs">
          <TabsTrigger value="list" data-testid="inv-tab-list">All items</TabsTrigger>
          <TabsTrigger value="models" data-testid="inv-tab-models">By model</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4 space-y-3">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input
                placeholder="Search asset ID, model, serial, configuration…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10"
                data-testid="inventory-search"
              />
            </div>
            <Input
              placeholder="Batch (e.g. AA)"
              value={batchFilter}
              onChange={(e) => setBatchFilter(e.target.value)}
              className="h-10 sm:w-28"
              data-testid="inventory-batch-filter"
            />
            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger className="h-10 sm:w-40" data-testid="inventory-stock-filter">
                <SelectValue placeholder="Stock" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All stock</SelectItem>
                <SelectItem value="Available">Available</SelectItem>
                <SelectItem value="Sold">Sold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10 sm:w-40" data-testid="inventory-status-filter">
                <SelectValue placeholder="Testing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All testing</SelectItem>
                <SelectItem value="Pass">Pass</SelectItem>
                <SelectItem value="Failed">Failed</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {selectedIds.size > 0 && (
            <div>
              <Button variant="outline" onClick={() => setBulkConfirm(true)} className="h-9 gap-2 text-[#EF4444] border-[#EF4444]/40" data-testid="inv-delete-selected">
                <Trash2 className="w-4 h-4" /> Delete selected ({selectedIds.size})
              </Button>
            </div>
          )}
          <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-[#F8F9FA]">
                  <TableRow>
                    <TableHead className="w-10">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-[#0055FF] cursor-pointer" data-testid="inv-select-all" aria-label="Select all" />
                    </TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Asset ID</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Model</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Category</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Serial</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Configuration</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Testing</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Stock</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Sold To / ₹</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Remarks</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-10 text-sm text-[#64748B]">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                    </TableCell></TableRow>
                  ) : items.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-10 text-sm text-[#64748B]" data-testid="inventory-empty">
                      No inventory yet. Click "New entry" to get started.
                    </TableCell></TableRow>
                  ) : items.map((it) => (
                    <TableRow key={it.id} data-testid={`inventory-row-${it.asset_id}`} data-state={selectedIds.has(it.id) ? "selected" : undefined}>
                      <TableCell onClick={(ev) => ev.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(it.id)} onChange={() => toggleSelect(it.id)} className="w-4 h-4 accent-[#0055FF] cursor-pointer" data-testid={`inv-select-${it.asset_id}`} />
                      </TableCell>
                      <TableCell className="font-mono text-sm font-medium">{it.asset_id}</TableCell>
                      <TableCell className="text-sm">{it.model_name}</TableCell>
                      <TableCell className="text-xs text-[#475569]">{it.device_category || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-[#475569]">{it.serial_number}</TableCell>
                      <TableCell className="text-xs text-[#475569] max-w-[240px] truncate" title={it.configuration}>
                        {it.configuration}
                      </TableCell>
                      <TableCell><StatusBadge status={it.testing_status} testId={`badge-${it.asset_id}`} /></TableCell>
                      <TableCell><StatusBadge status={it.stock_status} /></TableCell>
                      <TableCell className="text-xs" data-testid={`sold-info-${it.asset_id}`}>
                        {it.stock_status === "Sold" && it.sold_to
                          ? <span><span className="font-medium">{it.sold_to}</span>{it.sale_price != null && <span className="font-mono text-[#0055FF]"> · ₹{Number(it.sale_price).toLocaleString("en-IN")}</span>}</span>
                          : <span className="text-[#94A3B8]">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-[#475569] max-w-[160px] truncate" title={it.sale_remarks || ""} data-testid={`remarks-${it.asset_id}`}>
                        {it.sale_remarks || <span className="text-[#94A3B8]">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          {it.stock_status === "Sold" ? (
                            <Button variant="ghost" size="sm" onClick={() => openSell(it)} title="Edit sale details" data-testid={`edit-sale-${it.asset_id}`}>
                              <ShoppingCart className="w-4 h-4 text-[#0F766E]" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => openSell(it)} disabled={it.testing_status !== "Pass"} title={it.testing_status !== "Pass" ? "Only Pass items can be sold" : "Sell this item"} data-testid={`sell-${it.asset_id}`}>
                              <ShoppingCart className="w-4 h-4 text-[#16A34A]" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-${it.asset_id}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="sm" onClick={() => setDeleteId(it.id)} data-testid={`delete-${it.asset_id}`}>
                              <Trash2 className="w-4 h-4 text-[#EF4444]" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="models" className="mt-4">
          <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
            <Table>
              <TableHeader className="bg-[#F8F9FA]">
                <TableRow>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider">Model</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Total</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Available</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Sold</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Holding</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.models.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-[#64748B]" data-testid="inv-models-empty">No inventory yet.</TableCell></TableRow>
                ) : summary.models.map((m) => (
                  <TableRow key={m.model_name} data-testid={`inv-model-row-${m.model_name}`}>
                    <TableCell className="text-sm font-medium">{m.model_name}</TableCell>
                    <TableCell className="text-right font-mono">{m.total}</TableCell>
                    <TableCell className="text-right font-mono text-[#22C55E]">{m.available}</TableCell>
                    <TableCell className="text-right font-mono text-[#0055FF]">{m.sold}</TableCell>
                    <TableCell className="text-right font-mono text-[#F59E0B]">{m.holding}</TableCell>
                  </TableRow>
                ))}
                {summary.models.length > 0 && (
                  <TableRow className="bg-[#F8F9FA] font-medium">
                    <TableCell className="text-sm">Total</TableCell>
                    <TableCell className="text-right font-mono">{summary.totals.total}</TableCell>
                    <TableCell className="text-right font-mono text-[#22C55E]">{summary.totals.available}</TableCell>
                    <TableCell className="text-right font-mono text-[#0055FF]">{summary.totals.sold}</TableCell>
                    <TableCell className="text-right font-mono text-[#F59E0B]">{summary.totals.holding}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <InventoryFormDialog
        open={openSheet}
        onOpenChange={setOpenSheet}
        editing={editing}
        form={form}
        setForm={setForm}
        saving={saving}
        onSave={save}
        previewAssetId={previewAssetId}
        categories={categories}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete inventory item?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} selected item{selectedIds.size > 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes the selected items (including any sold records and their sale entries). This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-inv-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-inv-bulk-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SellDialog
        sellItem={sellItem}
        sellForm={sellForm}
        setSellForm={setSellForm}
        selling={selling}
        canReturnToStock={isAdmin}
        onClose={() => setSellItem(null)}
        onSave={saveSell}
        onReturnToStock={() => setUnsellConfirm(sellItem)}
      />

      <AlertDialog open={!!unsellConfirm} onOpenChange={(o) => !o && setUnsellConfirm(null)}>
        <AlertDialogContent data-testid="unsell-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Return item to stock?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move <span className="font-medium">{unsellConfirm?.model_name} ({unsellConfirm?.asset_id})</span> back to Available and permanently delete its sale record. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => unsell(unsellConfirm)} className="bg-[#D97706] hover:bg-[#B45309]" data-testid="confirm-unsell">Return to stock</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
